import { useState, useEffect, lazy, Suspense } from "react";
import logo from "@/assets/logo.svg";
import { Link, useLocation } from "react-router-dom";
import { User, Menu, X, LogIn, Shield, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/use-admin";

const NotificationBell = lazy(() => import("@/components/NotificationBell"));

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/explore", label: "Explore" },
  { to: "/feed", label: "Feed" },
  { to: "/editor", label: "Editor" },
  { to: "/upload", label: "Upload" },
  { to: "/servers", label: "Servers" },
  { to: "/ai-generator", label: "AI Generator" },
  { to: "/leaderboard", label: "🏆 Leaderboard" },
  { to: "/emerald-rank", label: "💎 Emerald" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('light') ? 'light' : 'dark';
    }
    return 'light';
  });
  const location = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.classList.toggle('light', next === 'light');
    localStorage.setItem('theme', next);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-glass-border/50">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:text-sm focus:font-medium">
        Skip to content
      </a>
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <img src={logo} alt="Minecraft Skin World" className="w-10 h-10 rounded-lg" />
          <span className="text-lg font-bold hidden sm:inline tracking-wider" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '0.75rem', letterSpacing: '0.05em', textShadow: '2px 2px 0px hsl(var(--primary) / 0.5)' }}>
            <span className="text-primary">MC</span>
            <span className="text-foreground"> Skin </span>
            <span className="text-secondary">World</span>
          </span>
          <span className="text-sm font-bold sm:hidden tracking-wider" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '0.6rem', textShadow: '2px 2px 0px hsl(var(--primary) / 0.5)' }}>
            <span className="text-primary">MSW</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                location.pathname === link.to
                  ? "bg-primary/20 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Toggle theme" className="text-muted-foreground hover:text-foreground">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Suspense fallback={<div className="w-10 h-10" />}>
            <NotificationBell />
          </Suspense>
          {isAdmin && (
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="text-primary" aria-label="Admin dashboard">
                <Shield className="w-5 h-5" />
              </Button>
            </Link>
          )}
          {user ? (
            <Link to="/profile">
              <Button className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0 hover:opacity-90 transition-opacity">
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
            </Link>
          ) : (
            <Link to="/auth">
              <Button className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0 hover:opacity-90 transition-opacity">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-foreground p-2" aria-label="Toggle navigation menu">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden glass border-t border-glass-border/50 animate-fade-in">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.to
                    ? "bg-primary/20 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={toggleTheme}
              className="px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex items-center gap-2"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>
            <Link to={user ? "/profile" : "/auth"} onClick={() => setMobileOpen(false)}>
              <Button className="mt-2 w-full bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0">
                <User className="w-4 h-4 mr-2" />
                {user ? "Profile" : "Sign In"}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
